import { LinkItem } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import React, { KeyboardEvent, MouseEvent, useCallback } from 'react';
import { useRouter } from 'react-resource-router';

import { useAnalytics } from '@townsquare/analytics';
import { openInNewTab } from '@townsquare/ui-interactions';

import { IconPlacement, SideNavItem } from './types';

// When the old colors are removed, this override can be removed.
const blueHoverCssOverride = (isSelected: boolean) => ({
  ...(isSelected
    ? {
        backgroundColor: `${token('color.background.selected')} !important`,
        fontWeight: token('font.weight.medium'),
        '&:hover': {
          backgroundColor: `${token('color.background.selected.hovered')} !important`,
        },
        '&:active': {
          backgroundColor: `${token('color.background.selected.pressed')} !important`,
        },
      }
    : {
        '&:hover': {
          backgroundColor: token('color.background.neutral.subtle.hovered'),
        },
        '&:active': {
          backgroundColor: token('color.background.neutral.subtle.pressed'),
        },
      }),
});

type Props = {
  selectedKey?: string;
  items: SideNavItem[];
  disableTooltip?: boolean;
  allowMetaClickHandler?: boolean;
};

export const SidebarItems = (props: Props) => {
  const analytics = useAnalytics();
  const [routerStore, routerActions] = useRouter();

  const onClick = useCallback(
    (item: SideNavItem, event: MouseEvent | KeyboardEvent) => {
      event.preventDefault();
      if (props.allowMetaClickHandler && event.metaKey) {
        openInNewTab(item.path);
      } else {
        void routerActions.push(item.path);
      }
      void analytics.ui('sideNavItem', 'clicked', {
        screen: routerStore.route.name,
        navName: item.analyticsName || item.name,
        navPath: item.path,
      });
    },
    [analytics, props.allowMetaClickHandler, routerActions, routerStore.route.name],
  );

  return (
    <>
      {props.items.map(item => {
        const isSelected = item.matches?.some(match => match === props.selectedKey) || props.selectedKey === item.key;
        const iconPlacement = item.iconPlacement ?? IconPlacement.ICON_BEFORE;
        const content = (
          <LinkItem
            key={item.key}
            isSelected={isSelected}
            iconAfter={iconPlacement === IconPlacement.ICON_AFTER && item.icon}
            iconBefore={iconPlacement === IconPlacement.ICON_BEFORE && item.icon}
            onClick={mouseEvent => onClick(item, mouseEvent)}
            // eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
            cssFn={() => blueHoverCssOverride(isSelected)}
            testId={item.testId}
            href={item.path}
          >
            {item.name}
          </LinkItem>
        );

        return props.disableTooltip ? (
          content
        ) : (
          <Tooltip key={item.key} content={item.name}>
            {content}
          </Tooltip>
        );
      })}
    </>
  );
};
